import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { applyBranding } from "@/hooks/useBranding";
import { useRegionalImages } from "@/hooks/useRegionalImages";
import { RegionSelector, RegionalHeroImage } from "@/components/RegionalImagery";
import { PublicPage, PublicHeader, BrandMark } from "@/components/public/PublicShell";
import { Button } from "@/components/ui/button";
import { Sparkles, Briefcase, UserRound, ArrowRight, ShieldCheck, Eye, ScrollText, Search } from "lucide-react";
import { motion } from "framer-motion";

const PATHS = [
  { key: "recruiter", img: "hiring", fallback: "/cutout_hiring.png", icon: Briefcase, title: "We're Hiring", cta: "Enter recruiter workspace", to: "/get-started/recruiter" },
  { key: "candidate", img: "candidate", fallback: "/cutout_candidate.png", icon: UserRound, title: "I'm Looking for a Job", cta: "Start with your CV", to: "/onboarding" },
];
const FEATURES = [
  { icon: Sparkles, t: "Conversational hiring", d: "Draft roles, screen and shortlist by chatting with AI." },
  { icon: ShieldCheck, t: "Explainable & fair", d: "Evidence-backed scores with a human always in the loop." },
  { icon: Eye, t: "Advisory proctoring", d: "Disclosed, consented — signals, never verdicts." },
  { icon: ScrollText, t: "Governed by design", d: "Full audit trail, bias monitoring and data retention." },
];
const NAV = [["For employers", "/get-started/recruiter", "landing-nav-employers"], ["For candidates", "/onboarding", "landing-nav-candidates"], ["Open roles", "/careers", "landing-careers"]];

function PathChoice({ p, i, src, photo, onGo }) {
  const Icon = p.icon;
  return (
    <motion.button onClick={onGo} data-testid={`path-${p.key}`} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 160, damping: 20, delay: 0.2 + i * 0.12 }}
      whileHover={{ y: -8, rotate: i ? 0.8 : -0.8 }} whileTap={{ scale: 0.98 }} className="group relative rounded-2xl px-2 text-left">
      <div className="relative landing-stage">
        <div className="pointer-events-none absolute left-1/2 top-[26%] -translate-x-1/2 h-44 w-44 rounded-full bg-cyan-300/20 blur-[58px]" />
        <div className="pointer-events-none absolute left-[22%] top-6 h-40 w-40 rounded-full bg-emerald-500/15 blur-[60px] float-3d" />
        <div className="pointer-events-none absolute right-[20%] bottom-10 h-36 w-36 rounded-full bg-cyan-400/12 blur-[55px] float-3d" style={{ animationDelay: "1.6s" }} />
        <div className="pointer-events-none absolute left-1/2 bottom-3 -translate-x-1/2 h-[80%] w-[72%] rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.16),rgba(16,185,129,0.12)_45%,transparent_72%)] blur-2xl" />
        <div className="pointer-events-none absolute inset-x-0 mx-auto bottom-[6%] h-[64%] aspect-square rounded-full border border-white/10 ring-pulse" />
        <div className="pointer-events-none absolute inset-x-0 mx-auto bottom-[4%] h-[82%] aspect-square max-w-full rounded-full border border-dashed border-white/[0.08] spin-slow" />
        <div className="pointer-events-none absolute inset-0 people-float" style={{ animationDelay: `${i * 0.25}s, ${0.9 + i * 0.25 + i * 1.5}s` }} data-testid={`landing-${p.img}-float`}>
          <RegionalHeroImage key={src || p.img} src={src} fallback={p.fallback} testId={`landing-${p.img}-image`}
            className={photo ? "absolute inset-0 h-full w-full rounded-2xl object-cover border border-white/10 transition-transform duration-500 group-hover:-translate-y-1" : "absolute left-1/2 bottom-0 -translate-x-1/2 h-full w-auto max-w-full object-contain object-bottom drop-shadow-[0_20px_34px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:-translate-y-2"} />
        </div>
        <div className="absolute bottom-0 left-0 z-20 h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg"><Icon className="h-5 w-5 text-white" /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-xl font-display font-bold">{p.title}</span>
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-300 transition-[gap] group-hover:gap-2.5">{p.cta} <ArrowRight className="h-4 w-4" /></span>
      </div>
    </motion.button>
  );
}

function OpenRolesCta({ count, onGo }) {
  return (
    <div className="mt-5 flex justify-center rise-in" style={{ animationDelay: "400ms" }}>
      <button onClick={onGo} data-testid="landing-open-roles-cta"
        className="group inline-flex h-11 items-center gap-3 rounded-full border border-emerald-400/40 bg-emerald-500/10 pl-5 pr-1.5 text-[15px] font-semibold text-emerald-200 transition-[background-color,border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-emerald-300/70 hover:bg-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/20">
        <Search className="h-4 w-4" /> Browse open roles
        {count > 0 && <span className="rounded-full bg-emerald-400/15 px-2.5 py-0.5 text-xs font-medium text-emerald-200" data-testid="landing-open-roles-count">{count} open</span>}
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:translate-x-0.5"><ArrowRight className="h-4 w-4" /></span>
      </button>
    </div>
  );
}

function FeatureCard({ f, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ type: "spring", stiffness: 180, damping: 22, delay: i * 0.08 }}
      whileHover={{ y: -8 }} data-testid={`landing-feature-${i}`}
      className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.01] p-5 transition-[border-color,box-shadow] duration-300 hover:border-emerald-400/50 hover:shadow-xl hover:shadow-emerald-500/15">
      <span className="pointer-events-none absolute right-0 top-0 h-24 w-24 rounded-full bg-emerald-500/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 ring-1 ring-emerald-400/20 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6">
        <f.icon className="h-5 w-5 text-emerald-400" />
      </div>
      <div className="text-sm font-semibold group-hover:text-emerald-200 transition-colors">{f.t}</div>
      <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{f.d}</div>
    </motion.div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [hero, setHero] = useState({});
  const [roleCount, setRoleCount] = useState(0);
  const regional = useRegionalImages();
  useEffect(() => { applyBranding({ brand_primary: "#10B981" }); }, []);
  useEffect(() => { api.get("/public/hero-images").then(({ data }) => setHero(data || {})).catch(() => {}); }, []);
  useEffect(() => { api.get("/public/jobs").then(({ data }) => setRoleCount(Array.isArray(data) ? data.length : 0)).catch(() => {}); }, []);
  const toPaths = () => document.getElementById("paths")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <PublicPage className="relative overflow-hidden" data-testid="landing-page">
      <div className="pointer-events-none absolute inset-0 grain-overlay opacity-30" />
      <motion.div animate={{ x: [0, 40, 0], y: [0, 20, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} className="pointer-events-none absolute -top-40 inset-x-0 mx-auto h-[32rem] w-full max-w-lg rounded-full bg-emerald-500/15 blur-[140px]" />
      <motion.div animate={{ x: [0, -50, 0], y: [0, -30, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-teal-500/10 blur-[120px]" />

      <PublicHeader right={<>
        <Link to="/login" className="text-sm font-semibold hover:text-primary transition-colors" data-testid="landing-signin">Sign in</Link>
        <Button size="sm" className="rounded-full px-4 font-semibold" onClick={toPaths} data-testid="landing-get-started">Get started</Button>
      </>}>
        <BrandMark testId="landing-brand" />
        <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground" aria-label="Primary">
          {NAV.map(([label, to, id]) => <Link key={to} to={to} className="hover:text-foreground transition-colors" data-testid={id}>{label}</Link>)}
        </nav>
      </PublicHeader>

      <main className="relative z-10 px-6 sm:px-10 pt-6 pb-16 max-w-5xl mx-auto text-center">
        <h1 className="rise-in text-4xl sm:text-6xl font-display font-extrabold tracking-tight leading-[1.05]" style={{ animationDelay: "80ms" }}>
          What are you here to <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-emerald-400 bg-clip-text text-transparent">do today?</span>
        </h1>
        <p className="rise-in text-slate-300 text-base sm:text-lg mt-4 max-w-4xl mx-auto leading-relaxed" style={{ animationDelay: "160ms" }}>
          Hire faster with explainable AI, or land your next role with a profile that markets itself. Pick your path.
        </p>

        <div id="paths" className="grid sm:grid-cols-2 gap-5 mt-5 scroll-mt-24 stagger" data-testid="landing-paths">
          {PATHS.map((p, i) => <PathChoice key={p.key} p={p} i={i} src={regional.result?.images[p.img] || hero[`landing_${p.img}`]} photo={!!regional.result?.images[p.img]} onGo={() => nav(p.to)} />)}
        </div>

        <OpenRolesCta count={roleCount} onGo={() => nav("/careers")} />

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-16 text-left" data-testid="landing-features">
          {FEATURES.map((f, i) => <FeatureCard key={f.t} f={f} i={i} />)}
        </div>
      </main>

      <footer className="relative z-10 border-t border-border/70">
        <div className="mx-auto max-w-[1360px] px-4 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} HireStream</span>
          <RegionSelector {...regional} />
        </div>
      </footer>
    </PublicPage>
  );
}
